import {
	IdentityProviderProtocol,
	type IdentityProviderResource,
	type OidcProviderConfigurationView,
	SamlMetadataSourceKind,
	SamlNameIdFormat,
	type SamlProviderConfigurationView,
} from '@today/types'
import { useState } from 'react'
import type { OidcConfigurationSubmit, ProviderFormSubmit, SamlConfigurationSubmit } from './provider-form.types'

/** The SAML inputs, as typed. */
export interface SamlDraft {
	source: SamlMetadataSourceKind
	metadataUrl: string
	metadataXml: string
	entityId: string
	ssoUrl: string
	sloUrl: string
	certificatePem: string
	nameIdFormat: SamlNameIdFormat
	signRequests: boolean
	email: string
	groups: string
}

/** The OIDC inputs, as typed. `clientSecret` is never prefilled. */
export interface OidcDraft {
	issuer: string
	clientId: string
	clientSecret: string
	scopes: string
	authorizationEndpoint: string
	tokenEndpoint: string
	userinfoEndpoint: string
	jwksUri: string
	email: string
	groups: string
}

/** A trimmed value, or `undefined` when blank. */
const optional = (value: string): string | undefined => (value.trim() ? value.trim() : undefined)

/** Drop `undefined` entries so a blank optional input is omitted rather than sent. */
const compact = <T extends object>(value: T): T =>
	Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T

const mappingOf = (email: string, groups: string) => compact({ email: email.trim(), groups: optional(groups) })

const SAML_DEFAULTS: SamlDraft = {
	source: SamlMetadataSourceKind.URL,
	metadataUrl: '',
	metadataXml: '',
	entityId: '',
	ssoUrl: '',
	sloUrl: '',
	certificatePem: '',
	nameIdFormat: SamlNameIdFormat.EMAIL_ADDRESS,
	signRequests: false,
	email: 'email',
	groups: '',
}

const OIDC_DEFAULTS: OidcDraft = {
	issuer: '',
	clientId: '',
	clientSecret: '',
	scopes: 'openid email profile',
	authorizationEndpoint: '',
	tokenEndpoint: '',
	userinfoEndpoint: '',
	jwksUri: '',
	email: 'email',
	groups: '',
}

/** The stored SAML values. The document and the certificate PEM are never returned, so they start blank. */
const samlDraftOf = (view: SamlProviderConfigurationView): SamlDraft => ({
	...SAML_DEFAULTS,
	source: view.metadataSource,
	metadataUrl: view.metadataUrl ?? '',
	entityId: view.entityId ?? '',
	ssoUrl: view.ssoUrl ?? '',
	sloUrl: view.sloUrl ?? '',
	nameIdFormat: view.nameIdFormat,
	signRequests: view.signRequests,
	email: view.attributeMapping.email,
	groups: view.attributeMapping.groups ?? '',
})

/** The stored OIDC values. The client secret is never returned, so it starts blank. */
const oidcDraftOf = (view: OidcProviderConfigurationView): OidcDraft => ({
	...OIDC_DEFAULTS,
	issuer: view.issuer,
	clientId: view.clientId,
	scopes: view.scopes.join(' '),
	authorizationEndpoint: view.authorizationEndpoint ?? '',
	tokenEndpoint: view.tokenEndpoint ?? '',
	userinfoEndpoint: view.userinfoEndpoint ?? '',
	jwksUri: view.jwksUri ?? '',
	email: view.attributeMapping.email,
	groups: view.attributeMapping.groups ?? '',
})

/** The SAML `configuration` for the selected source; the other sources' inputs are ignored. */
export const samlConfigurationOf = (draft: SamlDraft): SamlConfigurationSubmit => {
	const common = { signRequests: draft.signRequests, attributeMapping: mappingOf(draft.email, draft.groups) }
	if (draft.source === SamlMetadataSourceKind.URL) return { metadataUrl: draft.metadataUrl.trim(), ...common }
	if (draft.source === SamlMetadataSourceKind.XML) return { metadataXml: draft.metadataXml, ...common }
	const fields = compact({
		entityId: draft.entityId.trim(),
		ssoUrl: draft.ssoUrl.trim(),
		sloUrl: optional(draft.sloUrl),
		certificatePem: draft.certificatePem.trim(),
		nameIdFormat: draft.nameIdFormat,
	})
	return { fields, ...common }
}

/** The OIDC `configuration`. A blank secret is omitted: on an edit that keeps the stored one. */
export const oidcConfigurationOf = (draft: OidcDraft): OidcConfigurationSubmit =>
	compact({
		issuer: draft.issuer.trim(),
		clientId: draft.clientId.trim(),
		clientSecret: draft.clientSecret ? draft.clientSecret : undefined,
		authorizationEndpoint: optional(draft.authorizationEndpoint),
		tokenEndpoint: optional(draft.tokenEndpoint),
		userinfoEndpoint: optional(draft.userinfoEndpoint),
		jwksUri: optional(draft.jwksUri),
		scopes: draft.scopes.split(/[\s,]+/).filter((scope) => scope.length > 0),
		attributeMapping: mappingOf(draft.email, draft.groups),
	})

const isSaml = (provider: IdentityProviderResource) => provider.protocol === IdentityProviderProtocol.SAML2
const isOidc = (provider: IdentityProviderResource) => provider.protocol === IdentityProviderProtocol.OIDC

/**
 * The form's local (UI-only) state, seeded from the provider being edited.
 * `configurationTouched` records whether any configuration input changed, so an
 * edit that only renames omits `configuration`.
 */
export function useProviderFormState(provider: IdentityProviderResource | null) {
	const [code, setCode] = useState(provider?.code ?? '')
	const [name, setName] = useState(provider?.name ?? '')
	const [protocol, setProtocol] = useState(provider?.protocol ?? IdentityProviderProtocol.SAML2)
	const [saml, setSaml] = useState(() =>
		provider && isSaml(provider) ? samlDraftOf(provider.configuration as SamlProviderConfigurationView) : SAML_DEFAULTS,
	)
	const [oidc, setOidc] = useState(() =>
		provider && isOidc(provider) ? oidcDraftOf(provider.configuration as OidcProviderConfigurationView) : OIDC_DEFAULTS,
	)
	const [configurationTouched, setConfigurationTouched] = useState(provider === null)

	const updateSaml = <K extends keyof SamlDraft>(key: K, value: SamlDraft[K]) => {
		setSaml((draft) => ({ ...draft, [key]: value }))
		setConfigurationTouched(true)
	}
	const updateOidc = <K extends keyof OidcDraft>(key: K, value: OidcDraft[K]) => {
		setOidc((draft) => ({ ...draft, [key]: value }))
		setConfigurationTouched(true)
	}
	const submission = (): ProviderFormSubmit => {
		const base = { code: code.trim(), name: name.trim(), protocol }
		if (!configurationTouched) return base
		const configuration =
			protocol === IdentityProviderProtocol.SAML2 ? samlConfigurationOf(saml) : oidcConfigurationOf(oidc)
		return { ...base, configuration }
	}

	return { code, setCode, name, setName, protocol, setProtocol, saml, updateSaml, oidc, updateOidc, submission }
}

export type ProviderFormState = ReturnType<typeof useProviderFormState>
